import parseJSON from '@utilities/parse-json';
import Events from '@utilities/events';
import updateGtmListingFiltersEvents from '@utilities/updateGtmListingFiltersEvents';
import updateGtmListingSortEvents from '@utilities/updateGtmListingSortEvents';
import parentUntil from '@utilities/parentUntil';
import gtmFooterSubscribeButtonClick from '@utilities/gtmFooterSubscribeButtonClick';

// Constants
const JS_HOOK_PRODUCT_TILE = '[js-hook-product-tile]:not([js-hook-gtm-id])';
const JS_HOOK_PROMOTION = '[js-hook-gtm-promotion]';
const JS_HOOK_GTM_CHECKOUT_CLICK = '[js-hook-gtm-checkout-click]';
const JS_HOOK_GTM_PAYMENT_MODERNIZATION_CLICK = '[js-hook-gtm-payment-modernization-click]';
const JS_HOOK_GTM_CHECKOUT_CLICK_DATA = '[js-hook-checkout-gtm-data]';
const JS_HOOK_GTM_CHECKOUT_BEGIN_DATA = '[js-hook-gtm-checkout-begin-data]';
const JS_HOOK_VIEW_CART = '[js-hook-view-cart]';
const JS_HOOK_UPDATE_CART_PRODUCT_QUANTITY = '[js-hook-update-cart-product-quantity]';
const JS_HOOK_PAYMENT_OPTION = '[js-hook-payment-method-input]';
const JS_HOOK_PAYMENT_OPTION_CUSTOM = '[js-hook-payment-method-custom-input]';
const JS_HOOK_CLICK_HEADER_LOGO = '[js-hook-click-header-logo]';
const JS_HOOK_CLICK_PROFESSIONAL = '[js-hook-click-professional]';
const JS_HOOK_CLICK_ICON_LIST_ITEM = '[js-hook-icon-list-item]';
const JS_HOOK_MEGA_MENU_ITEM = '[js-hook-mega-menu-item]';
const JS_HOOK_CATEGORY_NAME = '[js-hook-category-name]';
const JS_HOOK_MEGA_MENU_CATEGORY_ITEM = '[js-hook-mega-menu-category-item]';
const JS_HOOK_HERO_BUTTON = '[js-hook-hero-button]';
const JS_HOOK_BUTTON_LABEL = '[js-hook-button-label]';
const JS_HOOK_BASIC_TEXT = '[js-hook-basic-text]';
const JS_HOOK_SLIDER_SLIDES = '[js-hook-slider-slides]';
const JS_HOOK_SLIDE_TITLE = '[js-hook-slide-title]';
const JS_HOOK_CLICK_SHOP_BY_ROOM_GTM = '[js-hook-click-shop-by-room-gtm]';
const JS_HOOK_PRODUCT_TILE_BUTTON_TRIGGER = '[js-hook-product-tile-button-trigger]';
const JS_HOOK_PRODUCT_TILE_BUTTON = '[js-hook-product-tile-button]';
const JS_HOOK_SEO_DESCRIPTION = '[js-hook-seo-description]';
const JS_HOOK_TOGGLE = '[js-hook-toggle]';
const JS_HOOK_PLP_USER_TYPE = '[js-hook-plp-user-type]';
const JS_HOOK_FOOTER_CHANGE_LANGUAGE = '[js-hook-footer-change-language]';
const JS_HOOK_FOOTER_CHANGE_COUNTRY = '[js-hook-footer-change-country]';
const JS_HOOK_COUNTRY_SUBMIT = '[js-hook-country-submit]';
const JS_HOOK_COUNTRY_SELECTOR = '[js-hook-country-selector]';
const JS_HOOK_LANGUAGE_SELECTOR = '[js-hook-language-selector]';
const JS_HOOK_PRODUCT_ATTRIBUTE_TITLE = '[js-hook-product-attribute-title]';
const JS_HOOK_PDP_USER_TYPE = '[js-hook-pdp-user-type]';
const JS_HOOK_SEARCH_INPUT = '[js-hook-search-input]';
const JS_HOOK_CLICK_TRADE_PROJECT_GTM = '[js-hook-click-trade-project-gtm]';
const JS_HOOK_ACCORDION_ITEM = '[js-hook-accordion-item]';

/**
 * Change variation attribute by select change
 * @param {Object} element
 * @param {String} attributeHook
 * @return {Void}
 */
function changeVarioationAttributeBySelect(element, attributeHook) {
  // early return
  if (!element || !attributeHook) return;

  if (element.target.matches(`[variation-attribute-hook="${attributeHook}"], [variation-attribute-hook="${attributeHook}"] *`)) {
    const eventName = attributeHook === 'js-hook-model-info' ? 'click_model' : 'change_colour';
    const el = element.target.closest(`[variation-attribute-hook="${attributeHook}"]`);
    const option = el.options[el.selectedIndex].textContent.replace(/\n/g, '').trim();
    const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
      ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).dataset.productName
      : null;

    Events.$trigger('gtm::push', {
      data: {
        event: eventName,
        option,
        product
      }
    });
  }
};

/**
 * Change variation attribute by click on button
 * @param {Object} element
 * @param {String} attributeHook
 * @return {Void}
 */
function changeVarioationAttributeByButton(element, attributeHook) {
  // early return
  if (!element || !attributeHook) return;

  if (
    element.target.matches(`[variation-attribute-hook="${attributeHook}"], [variation-attribute-hook="${attributeHook}"] *`)
    && element.target.tagName !== 'SELECT'
  ) {
    const eventName = attributeHook === 'js-hook-model-info' ? 'click_model' : 'change_colour';
    const el = element.target.closest(`[variation-attribute-hook="${attributeHook}"]`);
    const option = el.querySelector('.csb__button-label')
      ? el.querySelector('.csb__button-label').textContent.replace(/\n/g, '').trim()
      : null;
    const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
      ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).dataset.productName
      : null;

    Events.$trigger('gtm::push', {
      data: {
        event: eventName,
        option,
        product
      }
    });
  }
};

class GTMEvents {
  constructor() {
    this.pageType = window.gtmData?.pageType;
    this.pagePathname = document.location.pathname;

    this.applicablePages = {
      impressionsEvent: ['checkout'],
      pageDesignerEvent: ['checkout']
    };

    this.bindEvents();

    if (!this.applicablePages.impressionsEvent.includes(this.pageType)) {
      GTMEvents.productTileEvents();
    }

    if (!this.applicablePages.pageDesignerEvent.includes(this.pageType)) {
      GTMEvents.pageDesignerEvents();
    }
    GTMEvents.cartEvents();
    GTMEvents.checkoutEvents();
    GTMEvents.updateCartProductQuantity();
    GTMEvents.paymentEvents();
    GTMEvents.paymentModernization();
    GTMEvents.clickHeaderLogo();
    GTMEvents.clickProfessional();
    GTMEvents.clickIconListItem();
    GTMEvents.clickMenuItem();
    GTMEvents.clickHeroButton();
    GTMEvents.clickBasickText();
    GTMEvents.clickSliderGTMClick();
    GTMEvents.clickRoom();
    GTMEvents.clickTooltop();
    GTMEvents.clickSEODescription();
    GTMEvents.clickPlpUserType();
    GTMEvents.clickFooterItem();
    GTMEvents.clickFooterSocial();
    GTMEvents.changeFooterLanguage();
    GTMEvents.changeFooterCountryLanguage();
    GTMEvents.changeFooterCountry();
    GTMEvents.clickPDPAccordion();
    GTMEvents.changePDPModel();
    GTMEvents.changePDPColor();
    GTMEvents.clickSparePartsPDP();
    GTMEvents.clickDownloadPDP();
    GTMEvents.clickImageZoomPDP();
    GTMEvents.clickPDPUserType();
    GTMEvents.searchNotFound();
    GTMEvents.clickTradeBecomeMember();
    GTMEvents.clickSearchResult();
    GTMEvents.clickTradeProject();
    GTMEvents.clickFAQ();
    GTMEvents.clickStory();
    GTMEvents.changePDPColorBB();

    updateGtmListingFiltersEvents();
    updateGtmListingSortEvents();
    gtmFooterSubscribeButtonClick();

    Events.$on('adyen-payment::payment-methods-loaded', () => GTMEvents.paymentEvents());
  }

  static paymentEvents() {
    const paymentInputs = [...document.querySelectorAll(JS_HOOK_PAYMENT_OPTION)];
    const paymentCustomInputs = [...document.querySelectorAll(JS_HOOK_PAYMENT_OPTION_CUSTOM)];
    const paymentAll = [...paymentInputs, ...paymentCustomInputs];
    const selected = paymentAll.find(el => el.checked);

    if (selected) {
      const paymentMethodId = selected.value;

      if (paymentMethodId) {
        GTMEvents.savePaymentMethod(paymentMethodId);
      }
    }

    paymentAll.forEach((el) => {
      el.addEventListener('change', function () {
        const paymentMethodId = this.value;

        if (paymentMethodId) {
          GTMEvents.savePaymentMethod(paymentMethodId);
        }
      });
    });
  }

  static clickHeaderLogo() {
    const headerLogoElement = document.querySelector(JS_HOOK_CLICK_HEADER_LOGO);

    if (headerLogoElement) {
      const locationURL = window.location.href;

      headerLogoElement.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_logo',
            URL: locationURL
          }
        });
      });
    }
  }

  static clickProfessional() {
    const professionalElement = document.querySelector(JS_HOOK_CLICK_PROFESSIONAL);

    if (professionalElement) {
      const locationURL = window.location.href;

      professionalElement.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_professional',
            URL: locationURL
          }
        });
      });
    }
  }

  static clickIconListItem() {
    const iconListItems = [...document.querySelectorAll(JS_HOOK_CLICK_ICON_LIST_ITEM)];

    if (iconListItems) {
      iconListItems.forEach((el) => {
        let elText = el.querySelector('.icon-list__item-label-text');

        elText = elText ? elText.textContent : null;

        el.addEventListener('click', () => {
          Events.$trigger('gtm::push', {
            data: {
              event: 'click_top_header',
              option: elText
            }
          });
        });
      });
    }
  }

  static clickMenuItem() {
    const menuItems = [...document.querySelectorAll(JS_HOOK_MEGA_MENU_ITEM)];
    const menuCategoryItems = [...document.querySelectorAll(JS_HOOK_MEGA_MENU_CATEGORY_ITEM)];

    if (menuItems) {
      menuItems.forEach((el) => {
        const isMobile = el.getAttribute('mobile');
        const menuItemElement = isMobile && isMobile === 'true' ? el : el.querySelector('.button__label');
        const menuItemName = menuItemElement && menuItemElement.dataset.englishLabel
          ? menuItemElement.dataset.englishLabel.replace(/\n/g, '').trim()
          : null;

        el.addEventListener('click', () => {
          Events.$trigger('gtm::push', {
            data: {
              event: 'click_menu',
              option: menuItemName
            }
          });
        });
      });
    }

    if (menuCategoryItems) {
      menuCategoryItems.forEach((el) => {
        const menuItemElement = el.querySelector(JS_HOOK_CATEGORY_NAME);
        const menuItemName = menuItemElement && menuItemElement.dataset.englishLabel
          ? menuItemElement.dataset.englishLabel.replace(/\n/g, '').trim()
          : null;

        el.addEventListener('click', (e) => {
          e.stopPropagation();
          Events.$trigger('gtm::push', {
            data: {
              event: 'click_menu',
              option: menuItemName
            }
          });
        });
      });
    }
  }

  static clickHeroButton() {
    const heroButton = document.querySelector(JS_HOOK_HERO_BUTTON);

    if (heroButton && !heroButton.hasAttribute('js-hook-gtm-click-trade-become-member')) {
      const banner = document.querySelector('.hero__title') ? document.querySelector('.hero__title').innerText : null;
      const cta = heroButton.querySelector(JS_HOOK_BUTTON_LABEL) ? heroButton.querySelector(JS_HOOK_BUTTON_LABEL).innerText : null;

      heroButton.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_hero_banner',
            banner,
            cta
          }
        });
      });
    }
  }

  static clickBasickText() {
    const body = document.querySelector('body');

    if (body.classList.contains('is-homepage')) {
      const basicTextButtons = [...document.querySelectorAll(JS_HOOK_BASIC_TEXT)];

      if (basicTextButtons) {
        basicTextButtons.forEach((el) => {
          const cta = el.querySelector(JS_HOOK_BUTTON_LABEL) ? el.querySelector(JS_HOOK_BUTTON_LABEL).innerText : null;
          const basicTextButtonContainer = parentUntil(el, '.o-grid');
          const blockName = basicTextButtonContainer.querySelector('.basic-text__title') ? basicTextButtonContainer.querySelector('.basic-text__title').innerText : 'Homepage Block';

          el.addEventListener('click', () => {
            Events.$trigger('gtm::push', {
              data: {
                event: 'click_hp_cta',
                cta,
                block_name: blockName
              }
            });
          });
        });
      }
    }
  }

  static clickSliderGTMClick() {
    const body = document.querySelector('body');

    if (body.classList.contains('is-homepage')) {
      const sliders = [...document.querySelectorAll(JS_HOOK_SLIDER_SLIDES)];

      if (sliders) {
        sliders.forEach((slider) => {
          const slides = [...slider.querySelectorAll('a')];

          slides.forEach((slide) => {
            const sliderContainer = parentUntil(slide, '.o-container');
            const sliderName = sliderContainer.querySelector('.slider__title') ? sliderContainer.querySelector('.slider__title').innerText : null;
            const option = slide.querySelector(JS_HOOK_SLIDE_TITLE) ? slide.querySelector(JS_HOOK_SLIDE_TITLE).innerText : null;
            const link = slide.href;

            slide.addEventListener('click', () => {
              Events.$trigger('gtm::push', {
                data: {
                  event: 'click_slider',
                  slider: sliderName,
                  option,
                  zone: link
                }
              });
            });
          })
        });
      }
    }
  }

  static clickRoom() {
    const body = document.querySelector('body');

    if (body.classList.contains('is-homepage')) {
      const slides = [...document.querySelectorAll(JS_HOOK_CLICK_SHOP_BY_ROOM_GTM)];

      if (slides) {
        slides.forEach((slide) => {
          const roomName = slide.querySelector(JS_HOOK_SLIDE_TITLE) ? slide.querySelector(JS_HOOK_SLIDE_TITLE).innerText : null;

          slide.addEventListener('click', () => {
            Events.$trigger('gtm::push', {
              data: {
                event: 'click_hp_room',
                option: roomName
              }
            });
          });
        });
      }
    }
  }

  // when clicks on the black button in the bottom right corner of image
  static clickTooltop() {
    const productTileButtonTriggers = [...document.querySelectorAll(JS_HOOK_PRODUCT_TILE_BUTTON_TRIGGER)];

    if (productTileButtonTriggers) {
      productTileButtonTriggers.forEach((productTileButtonTrigger) => {
        if (productTileButtonTrigger.classList.contains('product-tile-button__trigger')) {
          const productTileButton = parentUntil(productTileButtonTrigger, JS_HOOK_PRODUCT_TILE_BUTTON);
          const productName = productTileButton.querySelector(JS_HOOK_SLIDE_TITLE) ? productTileButton.querySelector(JS_HOOK_SLIDE_TITLE).innerText : null;

          productTileButtonTrigger.addEventListener('click', () => {
            Events.$trigger('gtm::push', {
              data: {
                event: 'click_tooltip',
                option: productName
              }
            });
          });
        }
      });
    }
  }

  static clickSEODescription() {
    const seoDescriptions = [...document.querySelectorAll(JS_HOOK_SEO_DESCRIPTION)];

    seoDescriptions.forEach(el => {
      const seoDescriptionBtn = el.querySelector(JS_HOOK_TOGGLE);

      if (seoDescriptionBtn) {
        const seoDescriptionPageType = el.dataset.pageType;

        switch (seoDescriptionPageType) {
          case 'plp':{
            seoDescriptionBtn.addEventListener('click', () => {
              Events.$trigger('gtm::push', {
                data: {
                  event: 'listing_click_text'
                }
              });
            });

            break;
          }
          case 'pdp':{
            seoDescriptionBtn.addEventListener('click', () => {
              const action = seoDescriptionBtn.getAttribute('aria-expanded') === 'true' ? 'open' : 'close';
              const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
                ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).textContent.replace(/\n/g, '').trim()
                : null;

              Events.$trigger('gtm::push', {
                data: {
                  event: 'click_pdp_description',
                  action,
                  product
                }
              });
            });

            break;
          }
          default:
            break;
        }
      }
    });
  }

  static clickPlpUserType() {
    const plpUserTypeButtons = [...document.querySelectorAll(JS_HOOK_PLP_USER_TYPE)];

    plpUserTypeButtons.forEach(el => {
      const plpUserType = el.hasAttribute('js-hook-open-chat') ? 'chat_with_flos' : 'consumer';

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_listing_user_type',
            option: plpUserType
          }
        });
      });
    });
  }

  static clickFooterItem() {
    const footerItems = [...document.querySelectorAll('.footer__links-list-item')];

    footerItems.forEach(el => {
      const option = el.firstElementChild ? el.firstElementChild.textContent.replace(/\n/g, '').trim() : null;

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_footer',
            option
          }
        });
      });
    });
  }

  static clickFooterSocial() {
    const footerSocials = [...document.querySelectorAll('.footer__socials-link')];

    footerSocials.forEach(el => {
      let {socialName} = el.dataset;

      socialName = socialName ? socialName.toLowerCase() : null;

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_social',
            social: socialName
          }
        });
      });
    });
  }

  static changeFooterLanguage() {
    const footerLanguages = [...document.querySelectorAll(JS_HOOK_FOOTER_CHANGE_LANGUAGE)];

    footerLanguages.forEach(el => {
      const option = el.querySelector(JS_HOOK_BUTTON_LABEL)
        ? el.querySelector(JS_HOOK_BUTTON_LABEL).textContent.replace(/\n/g, '').trim()
        : null;

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'change_language',
            option
          }
        });
      });
    });
  }

  static changeFooterCountry() {
    const footerCountries = [...document.querySelectorAll(JS_HOOK_FOOTER_CHANGE_COUNTRY)];

    footerCountries.forEach(el => {
      const option = el.querySelector(JS_HOOK_BUTTON_LABEL)
        ? el.querySelector(JS_HOOK_BUTTON_LABEL).textContent.replace(/\n/g, '').trim()
        : null;

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'change_country',
            option
          }
        });
      });
    });
  }

  static changeFooterCountryLanguage() {
    const countrySubmitBtn = document.querySelector(JS_HOOK_COUNTRY_SUBMIT);

    if (countrySubmitBtn) {
      countrySubmitBtn.addEventListener('click', () => {
        if (countrySubmitBtn.href) {
          let choosedCountry = document.querySelector(JS_HOOK_COUNTRY_SELECTOR);
          let choosedLanguage = document.querySelector(JS_HOOK_LANGUAGE_SELECTOR);

          // data normalization
          choosedCountry = choosedCountry.options[choosedCountry.selectedIndex].textContent.replace(/\n/g, '').trim();
          choosedLanguage = choosedLanguage.options[choosedLanguage.selectedIndex].textContent.replace(/\n/g, '').trim();

          Events.$trigger('gtm::push', {
            data: {
              event: 'change_country',
              option: choosedCountry
            }
          });

          Events.$trigger('gtm::push', {
            data: {
              event: 'change_language',
              option: choosedLanguage
            }
          });
        }
      });
    }
  }

  // Accordions on PDP below the image
  static clickPDPAccordion() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-product-detail-accordion], [js-hook-product-detail-accordion] *')) {
        const el = e.target.closest('[js-hook-product-detail-accordion]');
        const option = el.querySelector('.accordion__item-override-title')
          ? el.querySelector('.accordion__item-override-title').textContent.replace(/\n/g, '').trim()
          : null;
        const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
          ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).textContent.replace(/\n/g, '').trim()
          : null;

        Events.$trigger('gtm::push', {
          data: {
            event: 'click_accordion',
            option,
            product
          }
        });
      }
    });
  }

  static changePDPModel() {
    document.addEventListener('change', (e) => changeVarioationAttributeBySelect(e, 'js-hook-model-info'));
    document.addEventListener('click', (e) => changeVarioationAttributeByButton(e, 'js-hook-model-info'));
  }

  static changePDPColor() {
    document.addEventListener('change', (e) => changeVarioationAttributeBySelect(e, 'js-hook-color-gtm'));
    document.addEventListener('click', (e) => changeVarioationAttributeByButton(e, 'js-hook-color-gtm'));
  }

  static changePDPColorBB() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-color-gtm-bb], [js-hook-color-gtm-bb] *')) {
        const el = e.target.closest('[js-hook-color-gtm-bb]');
        const option = el ? el.dataset.displayValue : null;
        const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
          ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).dataset.productName
          : null;

        Events.$trigger('gtm::push', {
          data: {
            event: 'change_colour',
            option,
            product
          }
        });
      }
    });
  }

  static clickSparePartsPDP() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-spare-parts-gtm-click], [js-hook-spare-parts-gtm-click] *')) {
        const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
          ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).textContent.replace(/\n/g, '').trim()
          : null;

        Events.$trigger('gtm::push', {
          data: {
            event: 'click_spare_parts',
            product
          }
        });
      }
    });
  }

  static clickDownloadPDP() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-download-gtm-click], [js-hook-download-gtm-click] *')) {
        const el = e.target.closest('[js-hook-download-gtm-click]');
        const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
          ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).textContent.replace(/\n/g, '').trim()
          : null;
        const fileName = el.href || null;

        Events.$trigger('gtm::push', {
          data: {
            event: 'download',
            product,
            file_name: fileName
          }
        });
      }
    });
  }

  static clickImageZoomPDP() {
    const {action} = document.querySelector('body').dataset;

    if (action === 'Product-Show') {
      document.addEventListener('click', (e) => {
        if (e.target.matches('[js-hook-image-zoom], [js-hook-image-zoom] *')) {
          const {order} = e.target.closest('[js-hook-image-zoom]').dataset;
          const product = document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE)
            ? document.querySelector(JS_HOOK_PRODUCT_ATTRIBUTE_TITLE).textContent.replace(/\n/g, '').trim()
            : null;

          Events.$trigger('gtm::push', {
            data: {
              event: 'click_zoom',
              order,
              product
            }
          });
        }
      });
    }
  }

  static clickPDPUserType() {
    const pdpUserTypeButtons = [...document.querySelectorAll(JS_HOOK_PDP_USER_TYPE)];

    pdpUserTypeButtons.forEach(el => {
      const pdpUserType = el.classList.contains('professional-trigger__button') ? 'professional' : 'consumer';

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_pdp_user_type',
            option: pdpUserType
          }
        });
      });
    });
  }

  static clickSearchResult() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-search-result], [js-hook-search-result] *')) {
        const el = e.target.closest('[js-hook-search-result]');
        const isShowAll = el.hasAttribute('js-hook-search-result-show-all');
        const windowGtmEvents = window.gtmEvents;
        const term = document.querySelector(JS_HOOK_SEARCH_INPUT) ? document.querySelector(JS_HOOK_SEARCH_INPUT).value : null;
        let option = '';

        if (isShowAll) {
          option = 'view all';
        } else if (windowGtmEvents) {
          const pageViewEvent = windowGtmEvents.find(event => event.event === 'page_view');

          if (pageViewEvent.pageName) {
            switch (pageViewEvent.pageName) {
              case 'ListingPage':{
                option = 'listing page';
                break;
              }
              case 'ProductPage':{
                option = 'product page';
                break;
              }
              default:
                break;
            }
          }
        }

        Events.$trigger('gtm::push', {
          data: {
            event: 'search_clicked_result',
            term,
            option
          }
        });
      }
    });
  }

  static searchNotFound() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-search-not-found], [js-hook-search-not-found] *')) {
        const el = e.target.closest('[js-hook-search-not-found]');
        const userType = el.hasAttribute('js-hook-open-chat') ? 'chat_with_flos' : 'professional';
        const modalSearch = document.querySelector('#modal-search');
        let term = null;

        if (modalSearch.classList.contains('modal--is-showing')) {
          term = document.querySelector(JS_HOOK_SEARCH_INPUT) ? document.querySelector(JS_HOOK_SEARCH_INPUT).value : null;
        } else {
          const {querystring} = document.querySelector('body').dataset;
          const urlParams = new URLSearchParams(querystring);

          term = urlParams && urlParams.get('q') ? urlParams.get('q') : null;
        }

        Events.$trigger('gtm::push', {
          data: {
            event: 'search_not_found',
            term,
            option: userType
          }
        });
      }
    });
  }

  static clickTradeBecomeMember() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-gtm-click-trade-become-member], [js-hook-gtm-click-trade-become-member] *')) {
        const el = e.target.closest('[js-hook-gtm-click-trade-become-member]');
        const position = el.parentNode.hasAttribute('js-hook-basic-text') ? 'text' : 'hero';

        Events.$trigger('gtm::push', {
          data: {
            event: 'click_become_member',
            position
          }
        });
      }
    });
  }

  static clickTradeProject() {
    const tradeProjects = [...document.querySelectorAll(JS_HOOK_CLICK_TRADE_PROJECT_GTM)];

    tradeProjects.forEach(el => {
      const option = el.querySelector(JS_HOOK_SLIDE_TITLE) ? el.querySelector(JS_HOOK_SLIDE_TITLE).innerText : null;

      el.addEventListener('click', () => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'click_project',
            option
          }
        });
      });
    });
  }

  static clickFAQ() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-click-faq-gtm], [js-hook-click-faq-gtm] *')) {
        const el = e.target.closest('[js-hook-click-faq-gtm]');
        let action = null;
        const faqAccordionContainer = parentUntil(el, JS_HOOK_ACCORDION_ITEM);
        const option = el.querySelector('.accordion__item-override-title')
          ? el.querySelector('.accordion__item-override-title').textContent.replace(/\n/g, '').trim()
          : null;

        // data normalization
        if (faqAccordionContainer) action = faqAccordionContainer.classList.contains('accordion__item--is-active') ? 'close' : 'open';

        Events.$trigger('gtm::push', {
          data: {
            event: 'click_faq',
            action,
            option
          }
        });
      }
    });
  }

  static clickStory() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[js-hook-click-story-gtm], [js-hook-click-story-gtm] *')) {
        const el = e.target.closest('[js-hook-click-story-gtm]');
        const option = el.querySelector(JS_HOOK_SLIDE_TITLE) ? el.querySelector(JS_HOOK_SLIDE_TITLE).innerText : null;

        Events.$trigger('gtm::push', {
          data: {
            event: 'click_stories',
            option
          }
        });
      }
    });
  }

  static paymentModernization() {
    if (document.querySelector(JS_HOOK_GTM_PAYMENT_MODERNIZATION_CLICK)) {
      document.querySelector(JS_HOOK_GTM_PAYMENT_MODERNIZATION_CLICK).addEventListener('click', () => {
        const dataPoint = document.querySelector(JS_HOOK_GTM_CHECKOUT_CLICK_DATA);
        const { ga4PaymentObj } = dataPoint.dataset;

        if (ga4PaymentObj) {
          const paymentType = document.querySelector('#selectedPaymentOption').value;
          let tempGa4PaymentObj = null;

          try {
            tempGa4PaymentObj = JSON.parse(ga4PaymentObj);

            if (tempGa4PaymentObj && tempGa4PaymentObj.ecommerce && paymentType) tempGa4PaymentObj.ecommerce.payment_type = paymentType.replace(/\n/g, '').trim();
          } catch (error) {
            tempGa4PaymentObj = window.ga4Data;
          }

          Events.$trigger('gtm::push', { data: {'ecommerce':null}});
          Events.$trigger('gtm::push', {
            data: tempGa4PaymentObj
          });
        }
      });
    }
  }

  static savePaymentMethod(paymentMethodId) {
    const { ga4Data } = window;
    if (ga4Data && ga4Data.ecommerce) {
      ga4Data.ecommerce.payment_type = paymentMethodId;
    }
  }

  static checkoutEvents() {
    [...document.querySelectorAll(JS_HOOK_GTM_CHECKOUT_CLICK)].forEach((el) => {
      const dataPoint = document.querySelector(JS_HOOK_GTM_CHECKOUT_CLICK_DATA);
      if (dataPoint) {
        const { modernization } = dataPoint.dataset;
        if (!modernization || modernization !== 'true') {
          const { gtmObj } = dataPoint.dataset;
          if (gtmObj) {
            el.addEventListener('click', () => {
              Events.$trigger('gtm::push', {
                data: JSON.parse(gtmObj)
              });
            });
          }
          const { ga4Obj } = dataPoint.dataset;
          if (ga4Obj) {
            el.addEventListener('click', () => {
              Events.$trigger('gtm::push', { data: {'ecommerce':null}});
              Events.$trigger('gtm::push', {
                data: JSON.parse(ga4Obj)
              });
            });
          }
          const { ga4PaymentObj } = dataPoint.dataset;
          if (ga4PaymentObj) {
            el.addEventListener('click', () => {
              Events.$trigger('gtm::push', { data: {'ecommerce':null}});
              Events.$trigger('gtm::push', {
                data: window.ga4Data
              });
            });
          }
        }
      }
    });

    [...document.querySelectorAll(JS_HOOK_GTM_CHECKOUT_BEGIN_DATA)].forEach(el => {
      const { gtmObj } = el.dataset;

      if (gtmObj) {
        el.addEventListener('click', () => {
          Events.$trigger('gtm::push', {
            data: JSON.parse(gtmObj)
          });
        });
      }
    })
  }

  static cartEvents(){
    const viewCart = document.querySelector(JS_HOOK_VIEW_CART);
    if (!viewCart){
      return;
    }
    const { gtmObj } = viewCart.dataset;
    const data = JSON.parse(gtmObj);

    Events.$trigger('gtm::push', { data: {'ecommerce':null}});
    Events.$trigger('gtm::push', {
      data
    });
  }

  // add_to_cart_ga Event on cart page
  static updateCartProductQuantity(){
    const updateCartProductQuantitySelects = [...document.querySelectorAll(JS_HOOK_UPDATE_CART_PRODUCT_QUANTITY)];

    if (!updateCartProductQuantitySelects.length) return;

    updateCartProductQuantitySelects.forEach(updateCartProductQuantitySelect => {
      updateCartProductQuantitySelect.addEventListener('change', () => {
        const lineItem = parentUntil(updateCartProductQuantitySelect, '[js-hook-cart-lineitem]');
        let { gtmObj } = lineItem.dataset;

        if (gtmObj) {
          gtmObj = JSON.parse(gtmObj);

          const productCurrency = gtmObj.currency;
          const productPrice = gtmObj.price;
          const productQuantity = updateCartProductQuantitySelect.selectedOptions[0].value;
          const productValue = productQuantity ? productPrice * productQuantity : productPrice;

          if (productQuantity) gtmObj.quantity = productQuantity;

          const fullGtmObj = {
            event: 'add_to_cart_ga',
            ecommerce: {
              value: productValue,
              currency: productCurrency,
              items: [gtmObj]
            }
          };

          Events.$trigger('gtm::push', { data: {'ecommerce':null}});
          Events.$trigger('gtm::push', { data: fullGtmObj});
        }
      });
    });
  }

  static productTileEvents() {
    const productTiles = [...document.querySelectorAll(JS_HOOK_PRODUCT_TILE)];

    if (!productTiles.length) {
      return;
    }

    const impressions = [];
    const items = [];
    productTiles.forEach(productTile => {
      const { gtmObj } = productTile.dataset;
      const gtmObjParsed = JSON.parse(gtmObj);
      const { ga4Obj } = productTile.dataset;
      const ga4ObjParsed = JSON.parse(ga4Obj);

      // fill for impression_view
      const impressionObj = gtmObjParsed.ecommerce.click.products[0];
      impressionObj.list = gtmObjParsed.ecommerce.click.actionField.list;
      impressions.push(impressionObj);
      const itemObj = ga4ObjParsed.ecommerce.items[0];
      items.push(itemObj);

      ga4ObjParsed.event = 'select_item'
      ga4ObjParsed.ecommerce.currency = itemObj.currency || '';
      ga4ObjParsed.ecommerce.value = itemObj.price || ''; 
      // impression_click
      productTile.addEventListener('click', () => {
        Events.$trigger('gtm::push', { data: {'ecommerce':null}});
        Events.$trigger('gtm::push', {
          data: ga4ObjParsed
        });
        Events.$trigger('gtm::push', {
          data: gtmObjParsed
        });
      });

    });

    Events.$trigger('gtm::push', {
      data: {
        event: 'impression_view',
        ecommerce: {
          impressions
        }
      }
    });
    Events.$trigger('gtm::push', { data: {'ecommerce':null}});
    Events.$trigger('gtm::push', {
      data:{
        event: 'view_item_list',
        ecommerce: {
          items
        }
      }
    });
  }

  static pageDesignerEvents() {
    const promotions = [...document.querySelectorAll(JS_HOOK_PROMOTION)];

    if (!promotions.length) {
      return;
    }

    const impressions = promotions.map((item) => parseJSON(item.dataset.gtmImpressionsEvent || item.dataset.gtm));

    promotions.forEach((promo) => {
      promo.addEventListener('click', (event) => {
        Events.$trigger('gtm::push', {
          data: {
            event: 'promotion_click',
            ecommerce: {
              promoClick: {
                promotions: [parseJSON(event.currentTarget.dataset.gtmImpressionsEvent || event.currentTarget.dataset.gtm)]
              }
            }
          }
        });
      });
    });

    Events.$trigger('gtm::push', {
      data: {
        event: 'promotion_view',
        ecommerce: {
          promoView: {
            promotions: impressions
          }
        }
      }
    });
  }

  static eventFromBackend(data) {
    const dataObj = typeof data === 'string' ? parseJSON(data) : data;
    const eventsObj = !Array.isArray(dataObj) ? parseJSON(dataObj) : dataObj;
    if (eventsObj) eventsObj.map((event) => Events.$trigger('gtm::push', { data: event }));
  }

  filterEvent(data) {
    const dataObj = typeof data === 'string' ? parseJSON(data) : data;

    Events.$trigger('gtm::push', {
      data: {
        event: 'filter_click',
        key: dataObj.key,
        value: dataObj.value,
        page: this.pagePathname
      }
    });
  }

  bindEvents() {
    Events.$on('gtm::eventFromBackend', (_, data) => GTMEvents.eventFromBackend(data));
    Events.$on('gtm::filter-event', (_, data) => this.filterEvent(data));
  }
}

export default new GTMEvents();
